import { makeLocalDateTime } from "@flockcover/flock-svc-core-types"
import { RebateCalculationPeriod } from "@shared/platform-api/generated"
import { kmToMileConverterWithDecimals } from "@shared/utils/formatters/kmToMiles"
import { RebatesSummaryData, RebateSummaryPeriodData } from "./types"

const makeMonthlyRebatesData = (data: RebateCalculationPeriod[]): ReadonlyArray<RebateSummaryPeriodData> =>
  data.reduce<RebateSummaryPeriodData[]>((acc, month) => {
    const { calculation, endTime } = month
    if (calculation) {
      const { grossRebateAmount, lossRatio } = month
      const { expectedMileage, totalMileage, fleetSafetyScore } = calculation
      const prevMonth = acc[acc.length - 1]
      const prevAccumulatedExpected = prevMonth?.accumulatedExpectedMileage || 0
      const prevMileage = prevMonth?.accumulatedMileage || 0

      acc.push({
        accumulatedExpectedMileage: prevAccumulatedExpected + kmToMileConverterWithDecimals(expectedMileage),
        accumulatedMileage: prevMileage + kmToMileConverterWithDecimals(totalMileage),
        accumulatedRebate: grossRebateAmount ? grossRebateAmount / 100 : 0,
        date: makeLocalDateTime(endTime),
        expectedMileage: kmToMileConverterWithDecimals(expectedMileage),
        lossRatio: formatLossRatio(lossRatio),
        mileage: kmToMileConverterWithDecimals(totalMileage),
        safetyScore: fleetSafetyScore ?? null,
      })
    } else {
      acc.push({
        accumulatedExpectedMileage: null,
        accumulatedMileage: null,
        accumulatedRebate: null,
        date: makeLocalDateTime(endTime),
        expectedMileage: null,
        lossRatio: null,
        mileage: null,
        safetyScore: null,
      })
    }
    return acc || []
  }, [])

const formatLossRatio = (lossRatio: number | undefined | null): number | null => {
  if (typeof lossRatio !== `number`) return null
  return parseFloat((lossRatio * 100).toFixed(1))
}

const getInitialSafetyScore = (data: RebateCalculationPeriod): number | null => data?.insightsInitialSafetyScore ?? null

export const makeRebatesData = (data: RebateCalculationPeriod[]): RebatesSummaryData => {
  const hasData = data.length
  const periods = hasData ? makeMonthlyRebatesData(data) : []
  const initialSafetyScore = hasData ? getInitialSafetyScore(data[data.length - 1]) : null

  if (!hasData || !data[0].calculation) {
    return {
      accumulatedExpectedMileage: null,
      accumulatedMileage: null,
      accumulatedRebate: null,
      changeInRebate: null,
      lossRatio: null,
      periods,
      safetyScore: null,
      safetyScoreThreshold: initialSafetyScore,
    }
  }

  const firstNotCalculatedPeriod = data.findIndex((period) => period.calculation === null)
  const lastCalculatedPeriodIndex = firstNotCalculatedPeriod === -1 ? periods.length - 1 : firstNotCalculatedPeriod - 1

  const lastCalculatedPeriod = periods[lastCalculatedPeriodIndex]
  const periodBeforeLastCalculatedPeriod =
    lastCalculatedPeriodIndex > 1 ? periods[lastCalculatedPeriodIndex - 1] : undefined
  const changeInRebate =
    lastCalculatedPeriod.accumulatedRebate! - (periodBeforeLastCalculatedPeriod?.accumulatedRebate ?? 0)

  return {
    accumulatedExpectedMileage: lastCalculatedPeriod.accumulatedExpectedMileage!,
    accumulatedMileage: lastCalculatedPeriod.accumulatedMileage!,
    accumulatedRebate: lastCalculatedPeriod.accumulatedRebate!,
    changeInRebate,
    lossRatio: lastCalculatedPeriod.lossRatio!,
    periods,
    safetyScore: initialSafetyScore ? lastCalculatedPeriod.safetyScore : null,
    safetyScoreThreshold: initialSafetyScore,
  }
}
